import { useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { usePatientCard } from '../../../../../hooks/usePatientCard';
import type { ExcludedTeethMap } from '../../../../../services/dmMonitoringControl';
import type { DmPatient } from '../../../../../services/dmPatient';
import { escapeAction, type ActionMode } from './actionSteps';
import { DmButton, DmErrorText, SavedText, Spinner } from './DmControlPrimitives';
import { DmFactsStrip } from './DmFactsStrip';
import { DmModalShell, type DmShellLayout } from './DmModalShell';
import {
    baselineOf,
    draftChanges,
    draftErrors,
    initialDraft,
    settingsEditable,
    type DmDraft,
    type DraftChange,
    type DraftPatch
} from './dmControlDraft';
import { NotificationsSection } from './excluded-teeth/NotificationsSection';
import { MonitoringPanel } from './MonitoringPanel';
import { useDmControlBusy } from './useDmControlMutation';
import { useDmControlSave } from './useDmControlSave';

interface FooterStatusProps {
    saving: boolean;
    error: string | null;
    saved: boolean;
    changes: readonly DraftChange[];
    idleText: string;
}

function FooterStatus({ saving, error, saved, changes, idleText }: FooterStatusProps) {
    if (saving) {
        return (
            <span className="inline-flex items-center gap-2 text-xs text-gray-500">
                <Spinner className="h-4 w-4 text-[#007E87]" />
                Saving to Dental Monitoring…
            </span>
        );
    }
    if (error) return <DmErrorText message={error} />;
    if (saved) return <SavedText />;
    if (changes.length) {
        return (
            <span className="text-xs text-gray-600">
                <span className="font-semibold text-gray-800">
                    {changes.length} unsaved change{changes.length === 1 ? '' : 's'}
                </span>
                <span className="text-gray-500">
                    {' '}
                    · {changes.map((change) => change.label).join(' · ')}
                </span>
            </span>
        );
    }
    return <span className="text-xs text-gray-500">{idleText}</span>;
}

interface DmControlMainProps {
    layout: DmShellLayout;
    patientId: string;
    patientName: string;
    patient: DmPatient;
    excluded: ExcludedTeethMap | null;
    excludedError: string | null;
    teethSaved: boolean;
    onEditTeeth: () => void;
    onClose: () => void;
    /** Unsaved changes, kept by the parent so they survive the excluded-teeth step. */
    edits: DmDraft | null;
    setEdits: Dispatch<SetStateAction<DmDraft | null>>;
}

// Opens the patient in Dental Monitoring, in a new tab, when the card has the link.
function DmProfileLink({ id, href }: { id: string | number; href: string | null }) {
    if (!href) return <>#{id}</>;
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-[#007E87] hover:underline"
        >
            #{id}
        </a>
    );
}

// The settings go into one draft the footer lists and saves together; status actions apply at once.
export function DmControlMain({
    layout,
    patientId,
    patientName,
    patient,
    excluded,
    excludedError,
    teethSaved,
    onEditTeeth,
    onClose,
    edits,
    setEdits
}: DmControlMainProps) {
    const base = useMemo(() => baselineOf(patient), [patient]);
    // The DM profile id lives on the patient card mirror, not in DM's live patient view.
    const monitoring = usePatientCard(patientId).data?.monitoring;
    const dmProfileId = monitoring?.dmProfileId ?? null;
    const dmLink = monitoring?.dmLink ?? null;
    const editable = settingsEditable(base);
    // Null means "as DM has it": the draft rebuilds from fresh data after a save or a discard.
    const saveState = useDmControlSave(patientId, () => setEdits(null));
    // The footer save or a status action: while either writes to DM, the rest of the modal waits.
    const busy = useDmControlBusy(patientId);
    const draft = edits ?? initialDraft(base);
    const set = (patch: DraftPatch) => {
        setEdits((current) => ({ ...(current ?? initialDraft(base)), ...patch }));
        if (!saveState.saving) saveState.reset();
    };
    const changes = draftChanges(base, draft);
    const errors = draftErrors(base, draft);
    const invalid = Object.keys(errors).length > 0;
    const saving = saveState.saving;
    const [actionMode, setActionMode] = useState<ActionMode>('idle');
    const [scanMessage, setScanMessage] = useState('');
    const onEscape = escapeAction(actionMode, scanMessage, changes.length);

    const idleText =
        base.status === 'stopped'
            ? 'Start monitoring again to edit its settings.'
            : base.status === 'not_started'
              ? 'Start monitoring to edit its settings.'
              : 'No unsaved changes.';
    const footer = (
        <>
            <div className="mr-auto min-w-0">
                <FooterStatus
                    saving={saving}
                    error={saveState.error}
                    saved={saveState.saved}
                    changes={changes}
                    idleText={idleText}
                />
            </div>
            {changes.length > 0 && !busy && (
                <DmButton
                    onClick={() => {
                        setEdits(null);
                        saveState.reset();
                    }}
                >
                    Discard
                </DmButton>
            )}
            {editable ? (
                <DmButton
                    kind="primary"
                    disabled={changes.length === 0 || invalid || busy}
                    loading={saving}
                    onClick={() => saveState.save({ base, draft })}
                >
                    {saving ? 'Saving…' : 'Save changes'}
                </DmButton>
            ) : (
                layout === 'modal' && <DmButton onClick={onClose}>Close</DmButton>
            )}
        </>
    );

    return (
        <DmModalShell
            layout={layout}
            title={`${patientName}`}
            subtitle={dmProfileId ? <DmProfileLink id={dmProfileId} href={dmLink} /> : undefined}
            subtitleInline
            headerless
            onClose={onClose}
            onDismiss={onEscape === 'back' ? () => setActionMode('idle') : undefined}
            dismissible={onEscape !== 'none'}
            busy={busy}
            footer={footer}
            pinned={
                <DmFactsStrip
                    patientId={patientId}
                    patient={patient}
                    onBack={layout === 'page' ? onClose : undefined}
                />
            }
        >
            <MonitoringPanel
                patientId={patientId}
                patient={patient}
                base={base}
                draft={draft}
                errors={errors}
                set={set}
                busy={busy}
                steps={{ mode: actionMode, setMode: setActionMode, scanMessage, setScanMessage }}
            />
            <NotificationsSection
                excluded={excluded}
                loadError={excludedError}
                readOnly={base.status === 'stopped'}
                justSaved={teethSaved}
                onEdit={onEditTeeth}
            />
        </DmModalShell>
    );
}
